// import { UilApps as DashboardIcon } from '@tooni/iconscout-unicons-react'
import {
  // UilNotebooks as ReadListsIcon,
  UilCog as AdminIcon,
  UilCommentsAlt as CoachingsIcon,
  UilUsersAlt as CommunityIcon,
  UilEditAlt as CreatorSpaceIcon,
  UilCog as MaxAdminIcon,
  UilChartBar as MediaAnalyticsIcon,
  UilBookOpen as MediaIcon,
  UilBookMedical as MyCoachingIcon,
  UilGift as PerksIcon,
  UilPlus as PlusIcon,
  UilSignin as SigninIcon,
  UilEnvelopeHeart as SubscriptionsIcon,
} from '@tooni/iconscout-unicons-react'

import isEmpty from 'lodash/fp/isEmpty'
import isString from 'lodash/fp/isString'

import type {
  Author,
  CoreUser,
  Edition,
  Newsletter,
  SidebarEntries,
  Translator,
} from '../types'
import type { FeatureFlags } from './feature_flags'

import { getAppUrlInfo, isLocalDev, isProd } from '.'
import {
  ANALYTICS_DASHBOARD_URL,
  DISCORD_URL,
  GRAFANA_ANALYTICS_SOURCE_IDS,
  GRAFANA_SOURCE_IDS,
  LOCAL_DOMAIN,
  MAIN_ANALYTICS_DASHBOARD_URL,
  PERKS_URL,
  PREPROD_DOMAIN,
  SNOWBALL_DOMAIN,
  SNOWBALL_MEDIA_DOMAIN,
} from '../constants'
import { Apps, Roles } from '../enums'
import { getRoleFlags } from './authentication'
import { hasActiveMaxSubscription, isUserPaying } from './user'

export { Link, redirect, usePathname, useRouter } from './translations'
export default function updateSeachParams(
  params: Record<string, unknown>,
  currentSearchParams?: URLSearchParams,
) {
  const newSearchParams = new URLSearchParams(currentSearchParams)

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) {
      newSearchParams.delete(key)
    } else {
      newSearchParams.set(key, String(value))
    }
  })

  return newSearchParams
}

export function getURLs(
  from: 'current' | 'local' | 'preprod' | 'prod' = 'current',
) {
  const { domain, host, port, protocol } = getAppUrlInfo()
  let hostOrDomain = host || domain
  switch (from) {
    case 'local': {
      hostOrDomain = LOCAL_DOMAIN

      break
    }

    case 'preprod': {
      hostOrDomain = (host || domain).includes('preprod-snowball.xyz')
        ? 'preprod-snowball.xyz'
        : PREPROD_DOMAIN

      break
    }

    case 'prod': {
      hostOrDomain = SNOWBALL_DOMAIN

      break
    }

    default: {
      break
    }
  }

  const assetsDomain =
    from === 'local' ||
    from === 'preprod' ||
    (from === 'current' &&
      (isLocalDev() ||
        (host || domain).includes('preprod-snowball.xyz') ||
        (host || domain).includes(PREPROD_DOMAIN) ||
        (host || domain).includes(LOCAL_DOMAIN)))
      ? `preprod.assets.${SNOWBALL_MEDIA_DOMAIN}`
      : `assets.${SNOWBALL_MEDIA_DOMAIN}`

  const withPort =
    !isEmpty(port) && isLocalDev() && port !== '80' && port !== '443'

  const appsPorts = {
    creators: withPort ? ':3001' : '',
    dashboard: withPort ? ':3004' : '',
    igloo: withPort ? ':3003' : '',
    max: withPort ? ':3000' : '',
    media: withPort ? ':3002' : '',
  }

  return {
    assets: `${protocol}://${assetsDomain}`,
    content: `${protocol}://content.${hostOrDomain}${appsPorts.media}`,
    creators: `${protocol}://creators.${hostOrDomain}${appsPorts.creators}`,
    dashboard: `${protocol}://dashboard.${hostOrDomain}${appsPorts.dashboard}`,
    forcedMedia: `${protocol}://${(host || domain).includes('content.') ? 'content' : 'media'}.${hostOrDomain}${appsPorts.media}`,
    igloo: `${protocol}://igloo.${hostOrDomain}${appsPorts.igloo}`,
    max: `${protocol}://max.${hostOrDomain}${appsPorts.max}`,
    media:
      process.env.MEDIA_URL ||
      process.env.NEXT_PUBLIC_MEDIA_URL ||
      `${protocol}://${(host || domain).includes('content.') ? 'content.' : 'media.'}${hostOrDomain}${appsPorts.media}`,
  }
}

export function getUrl(
  appName: keyof ReturnType<typeof getURLs>,
  path?: string,
  queryString?: string,
) {
  const URLs = getURLs()

  return `${URLs[appName] ?? ''}${path?.startsWith('/') ? '' : '/'}${path ?? ''}${isEmpty(queryString) ? '' : `?${queryString}`}`
}

export function getEditionUrl(
  edition: Edition | null | undefined,
  { forceId = false, fullQualified = true } = {},
) {
  const path =
    isEmpty(edition) || (isEmpty(edition.slug) && isEmpty(edition.uuid))
      ? 'articles'
      : `article/${forceId ? edition.uuid : edition.slug || edition.uuid}`

  return fullQualified ? getUrl(Apps.Media, path) : path
}

export function getNewsletterUrl(
  newsletter: Newsletter | null | undefined,
  { forceId = false, fullQualified = true } = {},
) {
  const path =
    isEmpty(newsletter) ||
    (isEmpty(newsletter.slug) && isEmpty(newsletter.uuid))
      ? 'newsletters'
      : `newsletter/${forceId ? newsletter.uuid : newsletter.slug || newsletter.uuid}`

  return fullQualified ? getUrl(Apps.Media, path) : path
}

export function getCreatorUrl(
  creator: Author | CoreUser | null | undefined,
  { forceId = false, fullQualified = true } = {},
) {
  const path =
    isEmpty(creator) /* isEmpty(creator.firstName) &&
      isEmpty(creator.lastName) && */ ||
    (isEmpty(creator.email) && isEmpty(creator.uuid))
      ? 'creators'
      : `creator/${forceId ? creator.uuid : /* `${creator.firstName}${!isEmpty(creator.firstName) && !isEmpty(creator.lastName) ? '-' : ''}${creator.lastName}` ||  */ creator.email || creator.uuid}`

  return fullQualified ? getUrl(Apps.Media, path) : path
}

export function getSigninUrl(
  appName: keyof ReturnType<typeof getURLs>,
  returnUrl?: string,
  queryString?: string,
) {
  return getUrl(
    appName,
    'signin',
    isEmpty(returnUrl)
      ? ''
      : `return=${returnUrl}${isEmpty(queryString) ? '' : `&${queryString}`}`,
  )
}

export function getSignupUrl(
  appName: keyof ReturnType<typeof getURLs>,
  returnUrl?: string,
  queryString?: string,
) {
  return getUrl(
    appName,
    'signup',
    isEmpty(returnUrl)
      ? ''
      : `return=${returnUrl}${isEmpty(queryString) ? '' : `&${queryString}`}`,
  )
}

export function getHomePage(fullQualified = false) {
  if (!isEmpty(process.env.NEXT_PUBLIC_HOME_PAGE)) {
    return process.env.NEXT_PUBLIC_HOME_PAGE
  }

  const { appName } = getAppUrlInfo()
  const URLs = getURLs()

  return fullQualified || appName !== Apps.Media ? URLs.media : ''
}

export function getPricingPage(fullQualified = false) {
  const homePage = getHomePage(fullQualified)

  if (!isEmpty(process.env.NEXT_PUBLIC_PRICING_PAGE)) {
    return process.env.NEXT_PUBLIC_PRICING_PAGE.startsWith('http')
      ? process.env.NEXT_PUBLIC_PRICING_PAGE!
      : `${homePage}${process.env.NEXT_PUBLIC_PRICING_PAGE}`
  }

  return `${homePage}/#plans`
}

export function getAboutPage(fullQualified = false) {
  const homePage = getHomePage(fullQualified)

  if (!isEmpty(process.env.NEXT_PUBLIC_ABOUT_PAGE)) {
    return process.env.NEXT_PUBLIC_ABOUT_PAGE.startsWith('http')
      ? process.env.NEXT_PUBLIC_ABOUT_PAGE!
      : `${homePage}${process.env.NEXT_PUBLIC_ABOUT_PAGE}`
  }

  return `${homePage}/about`
}

export function getPrivacyPage(fullQualified = false) {
  const homePage = getHomePage(fullQualified)

  if (!isEmpty(process.env.NEXT_PUBLIC_PRIVACY_PAGE)) {
    return process.env.NEXT_PUBLIC_PRIVACY_PAGE.startsWith('http')
      ? process.env.NEXT_PUBLIC_PRIVACY_PAGE!
      : `${homePage}${process.env.NEXT_PUBLIC_PRIVACY_PAGE}`
  }

  return `${homePage}/privacy`
}

export function getTCPage(fullQualified = false) {
  const homePage = getHomePage(fullQualified)

  if (!isEmpty(process.env.NEXT_PUBLIC_TERMS_AND_CONDITIONS_PAGE)) {
    return process.env.NEXT_PUBLIC_TERMS_AND_CONDITIONS_PAGE.startsWith('http')
      ? process.env.NEXT_PUBLIC_TERMS_AND_CONDITIONS_PAGE!
      : `${homePage}${process.env.NEXT_PUBLIC_TERMS_AND_CONDITIONS_PAGE}`
  }

  return `${homePage}/terms-and-conditions`
}

export function getLegalPage(fullQualified = false) {
  const homePage = getHomePage(fullQualified)

  if (!isEmpty(process.env.NEXT_PUBLIC_LEGAL_PAGE)) {
    return process.env.NEXT_PUBLIC_LEGAL_PAGE.startsWith('http')
      ? process.env.NEXT_PUBLIC_LEGAL_PAGE!
      : `${homePage}${process.env.NEXT_PUBLIC_LEGAL_PAGE}`
  }

  return `${homePage}/legal`
}

export function getBookPage(fullQualified = false) {
  const homePage = getHomePage(fullQualified)

  if (!isEmpty(process.env.NEXT_PUBLIC_BOOK_PAGE)) {
    return process.env.NEXT_PUBLIC_BOOK_PAGE.startsWith('http')
      ? process.env.NEXT_PUBLIC_BOOK_PAGE!
      : `${homePage}${process.env.NEXT_PUBLIC_BOOK_PAGE}`
  }

  return `${homePage}/book`
}

export function getBestAppsPage(fullQualified = false) {
  const homePage = getHomePage(fullQualified)

  if (!isEmpty(process.env.NEXT_PUBLIC_BEST_APPS_PAGE)) {
    return process.env.NEXT_PUBLIC_BEST_APPS_PAGE.startsWith('http')
      ? process.env.NEXT_PUBLIC_BEST_APPS_PAGE!
      : `${homePage}${process.env.NEXT_PUBLIC_BEST_APPS_PAGE}`
  }

  return `${homePage}/best-apps`
}

export function getSnowballPlusPage(
  fullQualified = false,
  origin: string | undefined = undefined,
) {
  const homePage = getHomePage(fullQualified)

  if (!isEmpty(process.env.NEXT_PUBLIC_PLUS_PAGE)) {
    return process.env.NEXT_PUBLIC_PLUS_PAGE.startsWith('http')
      ? process.env.NEXT_PUBLIC_PLUS_PAGE!
      : `${homePage}${process.env.NEXT_PUBLIC_PLUS_PAGE}`
  }

  return `${homePage}/snowball-plus${isEmpty(origin) ? '' : `?origin=${origin}`}`
}
export function getSnowballMaxPage(
  fullQualified = false,
  origin: string | undefined = undefined,
) {
  const homePage = getHomePage(fullQualified)

  if (!isEmpty(process.env.NEXT_PUBLIC_MAX_PAGE)) {
    return process.env.NEXT_PUBLIC_MAX_PAGE.startsWith('http')
      ? process.env.NEXT_PUBLIC_MAX_PAGE!
      : `${homePage}${process.env.NEXT_PUBLIC_MAX_PAGE}`
  }

  return `${homePage}/snowball-max${isEmpty(origin) ? '' : `?origin=${origin}`}`
}

export function getArticlesUrl(fullQualified = false) {
  const homePage = getHomePage(fullQualified)

  if (!isEmpty(process.env.NEXT_PUBLIC_ARTICLES_PAGE)) {
    return process.env.NEXT_PUBLIC_ARTICLES_PAGE.startsWith('http')
      ? process.env.NEXT_PUBLIC_ARTICLES_PAGE!
      : `${homePage}${process.env.NEXT_PUBLIC_ARTICLES_PAGE}`
  }

  return `${homePage}/articles`
}

export function getAnalyticsDashboardUrl(edition: string | Edition | null) {
  if (isEmpty(edition)) {
    return MAIN_ANALYTICS_DASHBOARD_URL.replace(
      '$__SOURCE__',
      isProd() ? GRAFANA_SOURCE_IDS.prod : GRAFANA_SOURCE_IDS.preprod,
    ).replace(
      '$__ANALYTICS_SOURCE__',
      isProd()
        ? GRAFANA_ANALYTICS_SOURCE_IDS.prod
        : GRAFANA_ANALYTICS_SOURCE_IDS.preprod,
    )
  }

  return ANALYTICS_DASHBOARD_URL.replace(
    '$__SOURCE__',
    isProd() ? GRAFANA_SOURCE_IDS.prod : GRAFANA_SOURCE_IDS.preprod,
  )
    .replace(
      '$__ANALYTICS_SOURCE__',
      isProd()
        ? GRAFANA_ANALYTICS_SOURCE_IDS.prod
        : GRAFANA_ANALYTICS_SOURCE_IDS.preprod,
    )
    .replace(
      '$__EDITION__',
      isString(edition) ? edition : (edition?.uuid as string),
    )
}

export async function getSidebarEntries<T extends CoreUser>(
  $t: Translator,
  user?: T | null,
  searchParams?: URLSearchParams,
  featureFlags?: FeatureFlags,
): Promise<SidebarEntries> {
  const isConnected = !isEmpty(user?.uuid)

  const { allowMaxSignups = false } = featureFlags ?? {}

  const {
    isActuallyAdmin,
    max: maxRoles,
    media: mediaRoles,
  } = getRoleFlags(user, searchParams)

  const hasMaxSubscription = hasActiveMaxSubscription(user, searchParams)
  const isPaying = isUserPaying(user, searchParams)

  const isMediaAdminOrCreatorOrCorrector =
    mediaRoles?.role === Roles.Admin ||
    mediaRoles?.role === Roles.Creator ||
    mediaRoles?.role === Roles.Corrector

  const isMaxAdminOrCoach =
    maxRoles?.role === Roles.Admin || maxRoles?.role === Roles.Coach

  /* eslint-disable line-comment-position */
  return [
    isConnected
      ? // ? [
        //     {
        //       appName: 'dashboard',
        //       href: '/',
        //       icon: <DashboardIcon />,
        //       label: $t('navigation.sidebar.dashboard'),
        //     },
        //   ]
        undefined
      : [
          {
            href: '/signin',
            icon: <SigninIcon />,
            label: $t('general.signin'),
          },
        ],

    isConnected && isMediaAdminOrCreatorOrCorrector
      ? [
          {
            appName: Apps.Creators,
            href: '/',
            icon: <CreatorSpaceIcon />,
            label: $t('navigation.sidebar.creators'),
          },
          mediaRoles?.role === Roles.Admin || mediaRoles?.role === Roles.Creator
            ? {
                href: getAnalyticsDashboardUrl(null),
                icon: <MediaAnalyticsIcon />,
                label: $t('navigation.sidebar.media_analytics'),
                target: '_blank',
              }
            : undefined,
        ]
      : undefined,

    isConnected && isMaxAdminOrCoach
      ? [
          {
            appName: Apps.Max,
            href: '/back-office/chat',
            icon: <CoachingsIcon />,
            label: $t('navigation.sidebar.coachings'),
          },
          maxRoles?.role === Roles.Admin
            ? {
                appName: Apps.Max,
                href: '/back-office/admin',
                icon: <MaxAdminIcon />,
                label: $t('navigation.sidebar.max_admin.home'),
                subEntries: [
                  {
                    appName: Apps.Max,
                    href: '/back-office/admin/settings',
                    label: $t('navigation.sidebar.max_admin.settings'),
                  },
                  {
                    appName: Apps.Max,
                    href: '/back-office/admin/tips',
                    label: $t('navigation.sidebar.max_admin.tips'),
                  },
                  {
                    appName: Apps.Max,
                    href: '/back-office/admin/campaigns',
                    label: $t('navigation.sidebar.max_admin.campaigns'),
                  },
                ],
              }
            : undefined,
        ]
      : undefined,

    [
      {
        appName: Apps.Media,
        href: getArticlesUrl(),
        icon: <MediaIcon />,
        label: $t('navigation.sidebar.media'),
        target: undefined,
      },
      isConnected
        ? {
            appName: Apps.Media,
            href: '/my-newsletters',
            icon: <SubscriptionsIcon />,
            label: $t('navigation.sidebar.subscriptions'),
          }
        : undefined,
      /* isConnected
        ? {
            appName: Apps.Media,
            href: '/read-lists',
            icon: <ReadListsIcon />,
            label: $t('navigation.sidebar.read_lists'),
            subEntries: [],
          }
        : undefined
      , */
      isPaying && isConnected && hasMaxSubscription
        ? {
            appName: Apps.Max,
            href: '/home',
            icon: <MyCoachingIcon />,
            label: $t('navigation.sidebar.my_coaching'),
          }
        : undefined,
      isPaying &&
      hasMaxSubscription &&
      !(mediaRoles?.role !== Roles.Member || maxRoles?.role !== Roles.Member)
        ? {
            href: PERKS_URL,
            icon: <PerksIcon />,
            label: $t('navigation.sidebar.perks'),
            target: '_blank',
          }
        : undefined,
    ],

    isConnected &&
    (!isPaying || (!hasMaxSubscription && allowMaxSignups)) &&
    !(mediaRoles?.role !== Roles.Member || maxRoles?.role !== Roles.Member)
      ? [
          isPaying
            ? undefined
            : {
                appName: Apps.Media,
                href: '/snowball-plus?origin=sidebar',
                icon: <PlusIcon />,
                label: $t('navigation.sidebar.plus'),
              },
          hasMaxSubscription || !allowMaxSignups
            ? undefined
            : {
                appName: Apps.Media,
                href: '/snowball-max',
                icon: <CoachingsIcon />,
                label: $t('navigation.sidebar.max'),
              },
        ]
      : undefined,

    isConnected && isPaying
      ? [
          {
            href: DISCORD_URL,
            icon: <CommunityIcon />,
            label: $t('navigation.sidebar.community'),
            target: '_blank',
          },
        ]
      : undefined,

    isActuallyAdmin
      ? [
          {
            appName: Apps.Igloo,
            href: '/',
            icon: <AdminIcon />,
            label: $t('navigation.sidebar.admin.home'),
            position: 'bottom',
            subEntries: [
              {
                appName: Apps.Igloo,
                href: '/users',
                label: $t('navigation.sidebar.admin.users'),
              },
            ],
          },
        ]
      : undefined,
  ] satisfies SidebarEntries
  /* eslint-enable line-comment-position */
}
